import { find, keys, map, pathOr, propEq } from 'ramda';
import { IS_ONPREMISE } from 'reactApp/appHelpers/configHelpers';
import { limitForPublic } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import { contactsSelectors } from 'reactApp/modules/contacts/contacts.selectors';
import type { DownloadLimit, PublicFile, PublicFolder, PublicState } from 'reactApp/modules/public/public.types';
import { getCurrentRouteId } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import type { RootState } from 'reactApp/store';
import type { Kind } from 'reactApp/types/Tree';
import { createSelector } from 'reselect';

const getPublicState = (state: RootState): PublicState => state.public;

export const getPublicItemById = createSelector(
    getPublicState,
    (state, id): string => id,
    (publicState, id) => {
        return publicState.list[id];
    }
);

export const getCurrentPublicFolder = createSelector(
    getCurrentRouteId,
    (state) => state,
    (routeId, state): PublicFolder | undefined => {
        if (!routeId) {
            return;
        }

        const item = getPublicItemById(state, routeId);

        if (!item) {
            return;
        }

        if (item.isFolder) {
            return item;
        }

        return getPublicItemById(state, item.parent) as PublicFolder;
    }
);

export const getCurrentPublicItem = createSelector(
    getCurrentRouteId,
    (state) => state,
    (routeId, state): PublicFolder | PublicFile | undefined => {
        if (!routeId) {
            return;
        }

        return getPublicItemById(state, routeId);
    }
);

export const getCurrentPublicItemType = createSelector(getCurrentPublicItem, (item) => {
    if (!item) {
        return;
    }

    if (!item.isFolder) {
        return 'file';
    }

    return item.type === 'album' ? 'album' : 'folder';
});

export const getPublicRootWeblink = createSelector(getPublicState, (publicState) => publicState.rootWeblink);

export const getPublicList = createSelector(getPublicState, (publicState) => publicState.list);

export const isPublicUploadEnabled = createSelector(getPublicRootWeblink, (rootWeblink): boolean => rootWeblink.isUploadEnabled);
/**
 * Чекаем флаг `weblinkDownloadable` на веблинке на онпремисе
 */
export const isWeblinkDownloadable = createSelector(getPublicRootWeblink, (rootWeblink): boolean => !!rootWeblink.weblinkDownloadable);
/**
 * Чекаем флаг `weblinkDownloadable` на веблинке на онпремисе
 *
 * В б2с не проверяем - всегда true
 */
export const isDownloadActionAvailable = createSelector(isWeblinkDownloadable, (isDownloadable): boolean =>
    IS_ONPREMISE ? isDownloadable : true
);
export const isPublicDownloadableEnabled = createSelector(
    getPublicRootWeblink,
    (rootWeblink): boolean => !!rootWeblink.weblinkDownloadable
);
export const getPublicExpires = createSelector(getPublicRootWeblink, (rootWeblink): number | undefined => rootWeblink.weblinkExpires);
export const isPublicAutodelete = createSelector(getPublicRootWeblink, (rootWeblink): boolean => Boolean(rootWeblink.isAutodelete));
export const getPublicOwner = createSelector(getPublicState, (publicState) => publicState.owner);

export const isOwnPublic = createSelector(getPublicRootWeblink, (rootWeblink): boolean => rootWeblink.isOwn);

export const getCurrentPublicFolderChilds = createSelector(getCurrentPublicFolder, (folder) => folder?.childs || []);

export const getAuthors = (state, folder: PublicFolder) => folder?.authors || [];

export const getFolderAuthors = createSelector(contactsSelectors.getContacts, getAuthors, (contacts = [], authors = {}) => {
    return map(
        (author) => ({
            email: author,
            name: pathOr(author, ['name'], find(propEq('email', author), contacts || [])),
        }),
        keys(authors)
    );
});

export const getPublicUploadLimit = createSelector(getPublicRootWeblink, (rootWeblink): number | undefined => rootWeblink.uploadLimit);

export const getIsSinglePublic = createSelector(
    (state: RootState) => getPublicItemById(state, getPublicRootWeblink(state).weblink),
    (rootItem) => rootItem && !rootItem.isFolder
);

export const getPublicBreadcrumbs = createSelector(getCurrentPublicFolder, getPublicRootWeblink, (publicState, rootWeblink) => {
    if (!publicState || !rootWeblink) {
        return [];
    }

    const { weblink, name } = rootWeblink;
    const currentWeblink = publicState.weblink.replace(weblink, '');

    const breadCrumbs = [
        {
            name,
            id: weblink,
            kind: 'folder' as Kind,
            storage: EStorageType.public,
        },
    ];

    const pathParts = currentWeblink.split('/').filter(Boolean);

    pathParts.reduce((fullPath, path) => {
        const currentPath = `${fullPath}/${path}`;

        breadCrumbs.push({
            name: path,
            id: currentPath,
            kind: 'folder' as Kind,
            storage: EStorageType.public,
        });

        return currentPath;
    }, weblink);

    return breadCrumbs;
});

export const getCurrentFolderCursor = createSelector(getCurrentPublicFolder, (folder): string | undefined => folder?.cursor);

export const geIsOpenDwhRadarSent = createSelector(getPublicState, (state): boolean => Boolean(state.isOpenDwhRadarSent));

export const forceShowDownloadAppButton = createSelector(getPublicState, (publicState): boolean =>
    Boolean(publicState.forceShowDownloadButton)
);

export const getIsRedirectFromViewer = createSelector(getPublicState, (state): boolean => Boolean(state.redirectFromViewer));

export const getPublicPin = createSelector(
    getPublicState,
    (state, publicId: string): string => publicId,
    (state, publicId): string => {
        return state.publicPasswords[publicId];
    }
);

export const getPublicLimits = createSelector(
    getPublicState,
    (state, publicId: string): string => publicId,
    (state, publicId): DownloadLimit => {
        return state.downloadLimits[publicId];
    }
);

// для информера
export const getFirstPublicWithDownloadLeftZero = createSelector(getPublicState, (state): any => {
    const objLimits = state.downloadLimits;
    for (const key in objLimits) {
        if (objLimits[key].left === 0) {
            return { [key]: objLimits[key] };
        }
    }
    return null;
});

// для списка файлов
export const getFirstPublicWithDownloadLimits = createSelector(getPublicState, (state): any => {
    const objLimits = state.downloadLimits;
    for (const key in objLimits) {
        if (objLimits[key].left < limitForPublic) {
            return { [key]: objLimits[key] };
        }
    }
    return null;
});
